import * as React from 'react';
import { useTheme } from '../../../../../theme';
import { Form, FormSpy } from 'react-final-form';
import Field from './Field/Field';
import { useTranslation } from 'react-i18next';
import {
	FfpAccountFieldEnum,
	FfpDefaultAccountQuery,
	NewFfpAccountParameters,
	OptionParameterFragment,
	useCitySuggestionsQuery,
	ValidationRuleFragment
} from '@websky/graphql';
import { InputType, PassengerFieldOption } from '../../../../../Passenger/types';
import cn from 'classnames';
import ModalConfirm from '../../../../../ModalConfirm';
import { ItemIncluded } from '../../../../../Icons';
import { FormApi } from 'final-form';
import { Tooltip } from '../../../../../BaseComponents';
import { API_DATE_FORMAT, convertGenderToCorrectValue, format } from '../../../../../utils';
import { parse } from 'date-fns';
import { AutocompleteInputChangeReason } from '@mui/material';
import { useDebounce } from '../../../../../hooks';
import { CdnIcon } from '@shared/ui';
import { parsePhoneNumber } from 'libphonenumber-js';
import { AgreementCheckboxType } from '../../../../../AgreementCheckboxWrapper/components/AgreementCheckboxWrapper.hook';
import { SafeHtml } from '@shared/ui';

export interface Props {
	toLogin?: () => void;
	initialValues?: FfpDefaultAccountQuery;
	onSubmitForm?: () => void;
	onCreateAccount: (params: NewFfpAccountParameters[]) => Promise<object>;
	success?: boolean;
	error?: string;
	closeError?: () => void;
	customFieldsGroup?: FfpAccountFieldEnum[][];
	formRef?: React.MutableRefObject<FormApi<Record<string, string>>>;
	isOpen?: boolean;
	onErrorsChanges?: (hasErrors: boolean) => void;
	checkboxes?: AgreementCheckboxType[];
}

interface Field {
	type?: FfpAccountFieldEnum;
	name?: string;
	value?: string;
	validationRules?: ValidationRuleFragment[];
}

const blocks = [
	{
		title: 'Your name, Cyrillic',
		groups: [[FfpAccountFieldEnum.LastName], [FfpAccountFieldEnum.FirstName], [FfpAccountFieldEnum.MiddleName]]
	},
	{
		title: 'Your name, Latin',
		subtitle: 'Please enter Full name in Latin transcription',
		groups: [
			[FfpAccountFieldEnum.LatinLastName],
			[FfpAccountFieldEnum.LatinFirstName],
			[FfpAccountFieldEnum.LatinMiddleName]
		]
	},
	{
		title: 'Document',
		groups: [
			[FfpAccountFieldEnum.Country],
			[FfpAccountFieldEnum.DocType],
			[FfpAccountFieldEnum.DocNumber],
			[FfpAccountFieldEnum.DocExpiryDate],
			[FfpAccountFieldEnum.DocIssued],
			[FfpAccountFieldEnum.DocIssuedBy]
		]
	},
	{
		title: 'Contacts',
		subtitle: 'How to Connect with You?',
		groups: [[FfpAccountFieldEnum.Email], [FfpAccountFieldEnum.Phone], [FfpAccountFieldEnum.CityID]]
	}
];

const CreateAccount: React.FC<Props> = props => {
	const theme = useTheme('Loyalty').LoyaltyConnect;

	const [citySearchValue, setCitySearchValue] = React.useState('');
	const debouncedCitySearchValue = useDebounce(citySearchValue, 300);

	const { t } = useTranslation('Loyalty');

	const getFieldType = (type: FfpAccountFieldEnum) => {
		switch (type) {
			case FfpAccountFieldEnum.BirthDate:
			case FfpAccountFieldEnum.DocExpiryDate:
			case FfpAccountFieldEnum.DocIssued:
				return InputType.Date;

			case FfpAccountFieldEnum.Phone:
				return InputType.Phone;

			case FfpAccountFieldEnum.Country:
			case FfpAccountFieldEnum.DocType:
				return InputType.Select;

			case FfpAccountFieldEnum.Gender:
				return InputType.Switch;

			case FfpAccountFieldEnum.CityID:
				return InputType.Autocomplete;

			default:
				return InputType.Text;
		}
	};

	const getOptions = (validationRules: ValidationRuleFragment[], values: any, type: FfpAccountFieldEnum) => {
		let options: OptionParameterFragment[];

		for (const rule of validationRules) {
			if (!rule.with || rule.with.every(condition => values[condition.name] === condition.value)) {
				options = rule.options;
				break;
			}
		}

		return options.map(option => {
			return {
				key: option.value,
				value: option.label
			};
		});
	};

	const fieldList = props.initialValues?.FfpDefaultAccount?.values;

	const { data: citySuggestions } = useCitySuggestionsQuery({
		variables: {
			query: debouncedCitySearchValue
		},
		skip: !fieldList?.some(field => field.type === FfpAccountFieldEnum.Phone) || !debouncedCitySearchValue
	});

	const BirthdayGenderFieldGroup = [FfpAccountFieldEnum.BirthDate, FfpAccountFieldEnum.Gender]
		.map(fieldType => {
			return fieldList?.find(param => param?.type === fieldType);
		})
		.filter(p => p !== undefined);

	const formData = React.useMemo(() => {
		if (!fieldList) {
			return null;
		}

		const fieldListMap = new Map(fieldList.map(field => [field.type, field]));

		const data = blocks
			.map(block => {
				const groups = block.groups
					.map(group => group.map(fieldType => fieldListMap.get(fieldType)).filter(Boolean))
					.filter(group => group.length);

				return { ...block, groups };
			})
			.filter(block => block.groups.length);

		if (BirthdayGenderFieldGroup.length && data[0]) {
			data[0].groups.push(BirthdayGenderFieldGroup);
		}

		return data;
	}, [fieldList]);

	const [formValidationErrors, setValidationErrors] = React.useState({});

	const formBlocksValidationData = React.useMemo(() => {
		if (!formData) {
			return null;
		}
		const list = new Map(Object.entries(formValidationErrors));
		const blocksList = formData.map(block => {
			const isValidBLock = block.groups.find(gr => {
				const groupHasErrors = gr.find(val => {
					return list.get(val.name);
				});

				return !!groupHasErrors;
			});

			return !!!isValidBLock;
		});

		return blocksList;
	}, [fieldList, formValidationErrors]);

	const onSubmit = (data: Record<string, string>) => {
		props.onSubmitForm?.();

		const params: NewFfpAccountParameters[] = [];
		const valuesMap = new Map(
			props.initialValues?.FfpDefaultAccount?.values?.map(value => [value.name, value]) ?? []
		);

		Object.entries(data).forEach(([name, value]) => {
			let actualValue = value ?? '';

			const { type } = valuesMap.get(name) ?? {};

			try {
				if (type === FfpAccountFieldEnum.CityID) {
					actualValue = ((value as unknown) as PassengerFieldOption)?.value;
				} else if (type === FfpAccountFieldEnum.PhoneCountry) {
					actualValue = parsePhoneNumber(data['phone'] as string)?.country;
				}
			} catch (e) {
				console.error(e);
			}

			params.push({
				type,
				name,
				value: (actualValue ?? '').trim()
			});
		});

		return props.onCreateAccount(params);
	};

	const initialValues: Record<string, string> = {};

	props.initialValues?.FfpDefaultAccount?.values.forEach(value => {
		if (value.type === FfpAccountFieldEnum.Gender && value.value) {
			initialValues[value.name] = convertGenderToCorrectValue(value.value);
		} else {
			initialValues[value.name] = value.value;
		}

		if (getFieldType(value.type) === 'date' && !!value.value) {
			try {
				initialValues[value.name] = format(parse(value.value, API_DATE_FORMAT, new Date()), 'dd.MM.yyyy');
			} catch (e) {
				initialValues[value.name] = format(parse(value.value, 'dd.MM.yyyy', new Date()), 'dd.MM.yyyy');
			}
		}
	});

	const getFieldOptions = (
		fieldType: InputType,
		values: Record<string, string>,
		validationRules?: ValidationRuleFragment[],
		type?: FfpAccountFieldEnum
	) => {
		if (fieldType === InputType.Select) {
			return getOptions(validationRules as any, values, type);
		}

		if (fieldType === InputType.Autocomplete && type === FfpAccountFieldEnum.CityID) {
			return (
				citySuggestions?.CitySuggestions?.cities.map(city => ({
					key: `${city.city.name} (${city.city.region}, ${city.country.name})`,
					value: city.city.id
				})) || []
			);
		}

		return undefined;
	};

	const onCityAutocompleteChange = (value: string, reason: AutocompleteInputChangeReason) => {
		// return if option was selected
		if (value && reason === 'reset') {
			return;
		}

		setCitySearchValue(value || '');
	};

	React.useEffect(() => {
		if (!props.isOpen && props.formRef.current) {
			setTimeout(() => {
				// без setTimeout ошибка "Cannot reset() in onSubmit(), use setTimeout(form.reset)"
				props.formRef.current.reset();
			}, 0);
		}
	}, [props.isOpen]);

	if (!formData) {
		return <></>;
	}

	return (
		<div className={theme.loyaltyConnect}>
			<Form onSubmit={onSubmit} initialValues={initialValues} validateOnBlur={true}>
				{({ handleSubmit, values, form }) => {
					props.formRef.current = form;

					return (
						<>
							<form onSubmit={handleSubmit}>
								<div className={theme.form__content}>
									{formData.map((block, index) => (
										<div className={theme.block} key={index}>
											<div className={theme.block__headerWrapper}>
												<div className={theme.block__header}>
													<div className={theme.block__header__titleWrapper}>
														<div className={theme.block__header__titleNumber}>
															{index + 1}
														</div>
														<div className={theme.block__header__title}>
															{t(block.title)}
														</div>
													</div>
													{formBlocksValidationData[index] && (
														<div className={theme.block__header__valid}>{ItemIncluded}</div>
													)}
													{!formBlocksValidationData[index] && (
														<Tooltip title={t('Show errors')}>
															<div
																className={theme.block__header__invalid}
																onClick={() => {
																	block.groups.forEach(gr => {
																		gr.forEach(it => {
																			form.blur(it.name);
																		});
																	});
																}}
															>
																<CdnIcon icon="Info/Warning" />
															</div>
														</Tooltip>
													)}
												</div>
												{block.subtitle && (
													<div className={theme.block__header__subtitle}>
														{t(block.subtitle.toString())}
													</div>
												)}
											</div>
											{block?.groups?.map((group, index) => {
												return (
													<div className={theme.groupCreate} key={index}>
														{group?.map(field => {
															const type = getFieldType(field.type);
															const isEmail = field.type === FfpAccountFieldEnum.Email;
															const isDisabled = isEmail && !!initialValues['email'];
															const options = getFieldOptions(
																type,
																values,
																field.validationRules,
																field.type
															);

															return (
																<div
																	data-field-name={field.name}
																	className={cn(theme.groupCreate__field, {
																		[theme.groupCreate__field_switch]:
																			field.type === FfpAccountFieldEnum.Gender
																	})}
																	key={field.name}
																>
																	<Field
																		name={field.name}
																		label={field.name}
																		type={type}
																		fieldType={field.type}
																		value={field.value || ''}
																		validationRules={field.validationRules as any}
																		options={options}
																		translateValue={
																			field.type === FfpAccountFieldEnum.DocType
																		}
																		disabled={isDisabled}
																		onAutocompleteInputChange={
																			onCityAutocompleteChange
																		}
																	/>
																</div>
															);
														})}
													</div>
												);
											})}
										</div>
									))}
								</div>

								<FormSpy
									subscription={{
										errors: true,
										hasValidationErrors: true,
										values: true,
										modified: true,
										active: true
									}}
									onChange={({ errors, hasValidationErrors }) => {
										setTimeout(() => {
											setValidationErrors(errors);
											props.onErrorsChanges?.(hasValidationErrors);
										}, 0);
									}}
								/>
							</form>
							{props.error && (
								<ModalConfirm
									isOpen={!!props.error}
									size="medium"
									title={t('Cannot create an account')}
									onClose={props.closeError}
									children={<SafeHtml tag="div" html={props.error} />}
									confirmBtn={t('Close')}
									onConfirm={props.closeError}
								/>
							)}
						</>
					);
				}}
			</Form>
		</div>
	);
};

export default CreateAccount;
